import {
  EXEC_DOC_KINDS,
  EXEC_DOC_STATUSES,
  type ExecInventory,
  type ExecItem,
  type ExecOverview,
  type ExecTemplate,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { UploadButton, useProjectDefault, useProjectsWith } from '../psd/shared';
import { ExecTransmittals } from './ExecTransmittals';

const TONE = { missing: 'danger', ready: 'success', na: 'neutral' } as const;

function Progress({ percent, label }: { percent: number; label: string }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
        <span className="shrink-0 font-semibold">{percent} %</span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-tile"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="h-full bg-accent" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function ItemCard({ item, onChanged }: { item: ExecItem; onChanged: () => void }) {
  const [naOpen, setNaOpen] = useState(false);
  const [reason, setReason] = useState('');
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<ExecItem>(`/documents/exec/items/${item.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setNaOpen(false);
      onChanged();
    },
  });
  return (
    <li className="grid gap-3 rounded-md border border-line p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium [overflow-wrap:anywhere]">{item.title}</p>
          <p className="mt-1 text-sm text-ink-2">
            {EXEC_DOC_KINDS[item.kind]}
            {item.normRef ? ` · ${item.normRef}` : ''}
            {item.required ? '' : ' · необязательный'}
          </p>
        </div>
        <Badge tone={item.overdue ? 'danger' : TONE[item.status]}>
          {item.overdue ? 'Просрочено' : EXEC_DOC_STATUSES[item.status]}
        </Badge>
      </div>
      <p className="text-sm text-ink-2">
        {item.objectName ? `${item.objectName} · ` : ''}
        Файлов: {item.filesCount}
        {item.inspectionNumber ? ` · акт ${item.inspectionNumber} подписан` : ''}
        {item.responsibleName ? ` · ${item.responsibleName}` : ''}
        {item.dueDate ? ` · срок ${date(item.dueDate)}` : ''}
        {item.status === 'na' && item.naReason ? ` · ${item.naReason}` : ''}
      </p>
      {item.canEdit ? (
        <div className="flex flex-wrap gap-2">
          <UploadButton
            url="/files"
            fields={{ entityType: 'exec_doc', entityId: item.id }}
            accept=".pdf,.xlsx,.docx,.jpg,.jpeg,.png"
            label="Приложить файл"
            onDone={() => patch.mutate({ status: 'ready' })}
          />
          {item.status !== 'missing' ? (
            <Button className="min-h-11" onClick={() => patch.mutate({ status: 'missing', naReason: null })}>
              Вернуть в «не хватает»
            </Button>
          ) : null}
          {item.status !== 'na' && item.required ? (
            <Button className="min-h-11" onClick={() => setNaOpen((v) => !v)}>
              Не требуется
            </Button>
          ) : null}
        </div>
      ) : null}
      {naOpen ? (
        <div className="grid gap-2">
          <Field label="Почему не требуется" htmlFor={`na-${item.id}`}>
            <Input
              id={`na-${item.id}`}
              className="!h-11"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div>
            <Button
              variant="primary"
              className="min-h-11"
              disabled={!reason.trim()}
              loading={patch.isPending}
              onClick={() => patch.mutate({ status: 'na', naReason: reason.trim() })}
            >
              Сохранить
            </Button>
          </div>
        </div>
      ) : null}
      {patch.error ? <ErrorBox error={patch.error} /> : null}
    </li>
  );
}

function ApplyTemplate({
  projectId,
  objects,
  onDone,
}: {
  projectId: string;
  objects: Array<{ id: string; name: string }>;
  onDone: () => void;
}) {
  const templates = useQuery({
    queryKey: ['exec-templates'],
    queryFn: () => api<ExecTemplate[]>('/documents/exec/templates'),
  });
  const works = useQuery({
    queryKey: ['exec-works', projectId],
    queryFn: () =>
      api<Array<{ id: string; code: string; name: string }>>(`/documents/exec/projects/${projectId}/works`),
  });
  const [f, setF] = useState({ templateId: '', objectId: '', activityId: '' });
  const apply = useMutation({
    mutationFn: () =>
      api<{ added: number; skipped: number }>(`/documents/exec/projects/${projectId}/apply`, {
        body: {
          templateId: f.templateId,
          objectId: f.objectId || null,
          activityId: f.activityId || null,
        },
      }),
    onSuccess: (r) => {
      toast(r.added ? `Добавлено позиций: ${r.added}` : 'Все позиции шаблона уже есть');
      onDone();
    },
  });
  const list = (templates.data ?? []).filter((t) => t.status === 'active');
  return (
    <Panel title="Добавить перечень по шаблону">
      <div className="grid gap-4">
        <Field label="Шаблон" htmlFor="ex-template">
          <Select
            id="ex-template"
            className="!h-11"
            value={f.templateId}
            onChange={(e) => setF({ ...f, templateId: e.target.value })}
          >
            <option value="">{list.length ? 'Выберите шаблон' : 'Шаблонов пока нет'}</option>
            {list.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} · {t.items.length} док.
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Объект" htmlFor="ex-object">
          <Select
            id="ex-object"
            className="!h-11"
            value={f.objectId}
            onChange={(e) => setF({ ...f, objectId: e.target.value })}
          >
            <option value="">Весь проект</option>
            {objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Работа графика" htmlFor="ex-work" hint="К какой работе относится перечень">
          <Select
            id="ex-work"
            className="!h-11"
            value={f.activityId}
            onChange={(e) => setF({ ...f, activityId: e.target.value })}
          >
            <option value="">Без привязки к работе</option>
            {works.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} {w.name}
              </option>
            ))}
          </Select>
        </Field>
        {apply.error ? <ErrorBox error={apply.error} /> : null}
        <div>
          <Button
            variant="primary"
            className="min-h-11"
            disabled={!f.templateId}
            loading={apply.isPending}
            onClick={() => apply.mutate()}
          >
            Добавить в перечень
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function Inventory({ projectId, objectId }: { projectId: string; objectId?: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['exec-inventory', projectId, objectId ?? ''],
    queryFn: () =>
      api<ExecInventory>(`/documents/exec/projects/${projectId}/inventory`, { query: { objectId } }),
    enabled: open,
    retry: false,
  });
  const inv = q.data;
  return (
    <Panel title="Опись пакета">
      <div className="grid gap-3">
        <p className="text-sm text-ink-2">
          Готовые документы по порядку со сквозным номером — для передачи заказчику, технадзору или приёмке.
        </p>
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button onClick={() => setOpen(true)} loading={q.isFetching}>
            Собрать опись
          </Button>
          {inv?.entries.length ? (
            <Button variant="secondary" onClick={() => window.print()}>
              Напечатать
            </Button>
          ) : null}
        </div>
        {q.error ? <ErrorBox error={q.error} /> : null}
        {inv ? (
          <>
            <p className="text-sm">
              {inv.complete
                ? 'Все обязательные документы готовы — пакет можно передавать.'
                : `Пакет неполный: не хватает ${inv.gaps.length}.`}
            </p>
            {inv.gaps.length ? (
              <ul className="list-disc pl-5 text-sm">
                {inv.gaps.map((g) => (
                  <li key={g.itemId} className="[overflow-wrap:anywhere]">
                    {g.title}
                    {g.activityName ? ` · ${g.activityName}` : ''}
                    {g.overdue ? ' · просрочено' : ''}
                  </li>
                ))}
              </ul>
            ) : null}
            {inv.entries.length ? (
              <ol className="grid gap-2">
                {inv.entries.map((e) => (
                  <li key={e.itemId} className="rounded-md border border-line p-3 text-sm">
                    <p className="font-medium [overflow-wrap:anywhere]">
                      {e.no}. {e.title}
                    </p>
                    <p className="text-ink-2">
                      {EXEC_DOC_KINDS[e.kind]}
                      {e.normRef ? ` · ${e.normRef}` : ''}
                      {e.activityName ? ` · ${e.activityName}` : ''}
                      {e.inspectionNumber ? ` · акт ${e.inspectionNumber} подписан` : ''}
                    </p>
                    {e.files.length ? (
                      <p className="[overflow-wrap:anywhere]">{e.files.map((f) => f.name).join(', ')}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyState title="Готовых документов пока нет." />
            )}
          </>
        ) : null}
      </div>
    </Panel>
  );
}

export function ExecDocsPage() {
  const search = useSearch({ strict: false }) as { project?: string; object?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { list } = useProjectsWith('documents.view');
  const projectId = search.project ?? '';
  const go = (next: { project?: string; object?: string }) =>
    navigate({ to: '/documents/exec', search: next as never, replace: true });
  useProjectDefault(list, undefined, projectId, (id) => void go({ project: id }));
  const project = list.find((p) => p.id === projectId);
  useCrumbs([{ label: 'Документы', to: '/documents' }, { label: 'Исполнительная документация' }]);
  const key = ['exec-overview', projectId, search.object ?? ''];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api<ExecOverview>(`/documents/exec/projects/${projectId}`, { query: { objectId: search.object } }),
    enabled: Boolean(projectId),
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['exec-overview'] });
  const o = q.data;
  const objects = (project?.objects ?? []).map((x) => ({ id: x.id, name: x.name }));
  return (
    <Page className="max-w-4xl">
      <PageHeader
        title="Исполнительная документация"
        subtitle="Что обязательно сдать, что уже есть и чего не хватает"
        actions={
          <Link to="/documents" className="inline-flex min-h-11 items-center underline">
            К документам проекта
          </Link>
        }
      />
      <div className="grid gap-5">
        <Panel title="Проект">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Проект" htmlFor="ex-project">
              <Select
                id="ex-project"
                className="!h-11"
                value={projectId}
                onChange={(e) => void go({ project: e.target.value || undefined })}
              >
                {list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Объект" htmlFor="ex-filter">
              <Select
                id="ex-filter"
                className="!h-11"
                value={search.object ?? ''}
                onChange={(e) => void go({ project: projectId, object: e.target.value || undefined })}
              >
                <option value="">Все объекты</option>
                {objects.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Panel>
        {q.isPending && projectId ? <Spinner /> : null}
        {q.error ? <ErrorBox error={q.error} /> : null}
        {o ? (
          <>
            <Panel title="Готовность">
              {o.summary.total ? (
                <div className="grid gap-4">
                  <Progress
                    percent={o.summary.percent}
                    label={`Готово ${o.summary.ready} из ${o.summary.total} обязательных`}
                  />
                  {o.byWork.length > 1 || o.byWork[0]?.activityId ? (
                    <ul className="grid gap-3">
                      {o.byWork.map((w) => (
                        <li key={w.activityId ?? 'none'}>
                          <Progress
                            percent={w.summary.percent}
                            label={`${w.activityName ?? 'Без привязки к работе'}: не хватает ${w.summary.missing}`}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : (
                <EmptyState title="Перечня пока нет. Добавьте перечень по шаблону ниже." />
              )}
            </Panel>
            {o.items.length ? (
              <Panel title="Документы" meta={String(o.items.length)}>
                <ul className="grid gap-3">
                  {o.items.map((i) => (
                    <ItemCard key={i.id} item={i} onChanged={refresh} />
                  ))}
                </ul>
              </Panel>
            ) : null}
            {o.items.length ? <Inventory projectId={projectId} objectId={search.object} /> : null}
            {o.items.length ? <ExecTransmittals projectId={projectId} objectId={search.object} /> : null}
            {o.canEdit && projectId ? (
              <ApplyTemplate projectId={projectId} objects={objects} onDone={refresh} />
            ) : null}
          </>
        ) : null}
      </div>
    </Page>
  );
}
